import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, Check, Zap, Activity,
  AlertTriangle, Brain, User, Utensils, BarChart2,
} from 'lucide-react';

// ─── Catégories ───────────────────────────────────────
const CATEGORIES = [
  { id: 'all',       label: 'Tous',              icon: BarChart2    },
  { id: 'urgence',   label: 'Urgence / Sepsis',  icon: Zap          },
  { id: 'douleur',   label: 'Douleur',           icon: Activity     },
  { id: 'risques',   label: 'Risques',           icon: AlertTriangle},
  { id: 'sedation',  label: 'Sédation',          icon: Brain        },
  { id: 'autonomie', label: 'Autonomie',         icon: User         },
  { id: 'nutrition', label: 'Nutrition',         icon: Utensils     },
];

const CAT_STYLE = {
  urgence:   { text: 'text-rose-600 dark:text-rose-400',     bg: 'bg-rose-50 dark:bg-rose-900/20',     dot: 'bg-rose-500',    activeSidebar: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800',   accent: 'border-l-2 border-l-rose-500'    },
  douleur:   { text: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-900/20', dot: 'bg-violet-500',  activeSidebar: 'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800', accent: 'border-l-2 border-l-violet-500'  },
  risques:   { text: 'text-amber-600 dark:text-amber-400',   bg: 'bg-amber-50 dark:bg-amber-900/20',   dot: 'bg-amber-500',   activeSidebar: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',   accent: 'border-l-2 border-l-amber-500'   },
  sedation:  { text: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-900/20', dot: 'bg-indigo-500',  activeSidebar: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800', accent: 'border-l-2 border-l-indigo-500'  },
  autonomie: { text: 'text-blue-600 dark:text-blue-400',     bg: 'bg-blue-50 dark:bg-blue-900/20',     dot: 'bg-blue-500',    activeSidebar: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',     accent: 'border-l-2 border-l-blue-500'    },
  nutrition: { text: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', dot: 'bg-emerald-500', activeSidebar: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800', accent: 'border-l-2 border-l-emerald-500' },
};

const RISK_STYLE = {
  normal:   { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', bar: 'from-emerald-400 to-emerald-500' },
  faible:   { bg: 'bg-teal-100 dark:bg-teal-900/30',      text: 'text-teal-700 dark:text-teal-300',       bar: 'from-teal-400 to-teal-500'       },
  modere:   { bg: 'bg-amber-100 dark:bg-amber-900/30',    text: 'text-amber-700 dark:text-amber-300',     bar: 'from-amber-400 to-amber-500'     },
  eleve:    { bg: 'bg-orange-100 dark:bg-orange-900/30',  text: 'text-orange-700 dark:text-orange-300',   bar: 'from-orange-400 to-orange-500'   },
  critique: { bg: 'bg-red-100 dark:bg-red-900/30',        text: 'text-red-700 dark:text-red-300',         bar: 'from-red-500 to-red-600'         },
};

// ─── Scores metadata ──────────────────────────────────
const SCORES_META = [
  { id: 'gcs',      category: 'urgence',   title: 'Glasgow Coma Scale',             abbrev: 'GCS',      range: '3–15',    desc: 'Niveau de conscience' },
  { id: 'qsofa',    category: 'urgence',   title: 'Quick SOFA',                     abbrev: 'qSOFA',    range: '0–3',     desc: 'Dépistage du sepsis' },
  { id: 'news2',    category: 'urgence',   title: 'National Early Warning Score 2', abbrev: 'NEWS2',    range: '0–20',    desc: 'Détérioration clinique' },
  { id: 'rass',     category: 'sedation',  title: 'Richmond Agitation-Sedation',    abbrev: 'RASS',     range: '-5 à +4', desc: 'Sédation / agitation' },
  { id: 'douleur',  category: 'douleur',   title: 'Échelle Numérique Douleur',      abbrev: 'EN',       range: '0–10',    desc: 'Auto-évaluation douleur' },
  { id: 'algoplus', category: 'douleur',   title: 'Algoplus',                       abbrev: 'Algo+',    range: '0–5',     desc: 'Douleur non-communicant' },
  { id: 'braden',   category: 'risques',   title: "Échelle de Braden",              abbrev: 'Braden',   range: '6–23',    desc: "Risque d'escarre" },
  { id: 'morse',    category: 'risques',   title: 'Morse Fall Scale',               abbrev: 'Morse',    range: '0–125',   desc: 'Risque de chute' },
  { id: 'nrs',      category: 'nutrition', title: 'Nutritional Risk Screening',     abbrev: 'NRS-2002', range: '0–7',     desc: 'Dépistage dénutrition' },
  { id: 'barthel',  category: 'autonomie', title: "Index de Barthel",               abbrev: 'Barthel',  range: '0–100',   desc: 'Autonomie dans les AVQ' },
];

// ─── Données scores ───────────────────────────────────

const GCS_DATA = {
  groups: [
    { id: 'eyes', label: 'Ouverture des yeux (Y)', options: [
      { value: 4, label: 'Spontanée',       desc: 'Sans stimulation' },
      { value: 3, label: 'À la voix',       desc: 'Sur ordre verbal' },
      { value: 2, label: 'À la douleur',    desc: 'Stimulation douloureuse' },
      { value: 1, label: 'Nulle',           desc: 'Aucune ouverture' },
    ]},
    { id: 'verbal', label: 'Réponse verbale (V)', options: [
      { value: 5, label: 'Orientée',        desc: 'Cohérente et orientée' },
      { value: 4, label: 'Confuse',         desc: 'Conversation incohérente' },
      { value: 3, label: 'Mots',            desc: 'Mots inappropriés' },
      { value: 2, label: 'Sons',            desc: 'Sons incompréhensibles' },
      { value: 1, label: 'Nulle',           desc: 'Aucune réponse' },
    ]},
    { id: 'motor', label: 'Réponse motrice (M)', options: [
      { value: 6, label: 'Sur ordre',       desc: 'Obéit aux ordres' },
      { value: 5, label: 'Localisée',       desc: 'Localise la douleur' },
      { value: 4, label: 'Retrait',         desc: 'Retrait en flexion' },
      { value: 3, label: 'Flexion',         desc: 'Décortication' },
      { value: 2, label: 'Extension',       desc: 'Décérébration' },
      { value: 1, label: 'Nulle',           desc: 'Aucune réponse' },
    ]},
  ],
  min: 3, max: 15,
  levels: [
    { max: 8,  risk: 'critique', label: 'Coma sévère',   desc: 'GCS ≤ 8 — Envisager intubation, surveillance continue.' },
    { max: 12, risk: 'modere',   label: 'Modéré',        desc: 'Altération modérée. Surveillance rapprochée, alerter le médecin.' },
    { max: 14, risk: 'faible',   label: 'Léger',         desc: 'Altération légère de la conscience. Réévaluation fréquente.' },
    { max: 15, risk: 'normal',   label: 'Normal',        desc: 'Conscience normale.' },
  ],
};

const QSOFA_ITEMS = [
  { id: 'fr',  label: 'Fréquence respiratoire ≥ 22/min',               desc: 'Tachypnée',                     points: 1 },
  { id: 'gcs', label: 'Glasgow < 15',                                    desc: 'Altération de la conscience',   points: 1 },
  { id: 'pas', label: 'Pression artérielle systolique ≤ 100 mmHg',     desc: 'Hypotension artérielle',        points: 1 },
];
const QSOFA_LEVELS = [
  { max: 1, risk: 'faible',   label: 'Faible risque',    desc: 'Risque faible de sepsis. Surveillance standard et réévaluation.' },
  { max: 2, risk: 'eleve',    label: 'Suspicion sepsis', desc: '≥ 2 critères : suspicion de sepsis. Alerter le médecin immédiatement.' },
  { max: 3, risk: 'critique', label: 'Sepsis probable',  desc: 'Sepsis probable. Urgence : hémocultures, lactates, ATB selon prescription.' },
];

const NEWS2_PARAMS = [
  { id: 'fr',         label: 'Fréquence respiratoire', unit: '/min', type: 'number', score: v => v <= 8 ? 3 : v <= 11 ? 1 : v <= 20 ? 0 : v <= 24 ? 2 : 3 },
  { id: 'spo2',       label: 'SpO₂',                  unit: '%',    type: 'number', score: v => v <= 91 ? 3 : v <= 93 ? 2 : v <= 95 ? 1 : 0 },
  { id: 'o2',         label: 'Supplémentation O₂',    unit: '',     type: 'toggle', options: ['Non', 'Oui'],   score: v => v === 'Oui' ? 2 : 0 },
  { id: 'temp',       label: 'Température',           unit: '°C',   type: 'number', step: 0.1, score: v => v <= 35.0 ? 3 : v <= 36.0 ? 1 : v <= 38.0 ? 0 : v <= 39.0 ? 1 : 2 },
  { id: 'pas',        label: 'PA systolique',         unit: 'mmHg', type: 'number', score: v => v <= 90 ? 3 : v <= 100 ? 2 : v <= 110 ? 1 : v <= 219 ? 0 : 3 },
  { id: 'fc',         label: 'Fréquence cardiaque',   unit: 'bpm',  type: 'number', score: v => v <= 40 ? 3 : v <= 50 ? 1 : v <= 90 ? 0 : v <= 110 ? 1 : v <= 130 ? 2 : 3 },
  { id: 'conscience', label: 'Conscience (ACVPU)',    unit: '',     type: 'select', options: ['A — Éveillé', 'C — Confusion nouvelle', 'V — Répond à la voix', 'P — Répond à la douleur', 'U — Inconscient'], score: v => v === 'A — Éveillé' ? 0 : 3 },
];
const NEWS2_LEVELS = [
  { max: 0,  risk: 'normal',   label: 'Bas',    desc: 'Score 0. Surveillance de routine.' },
  { max: 4,  risk: 'faible',   label: 'Faible', desc: 'Score 1–4. Évaluer l\'état toutes les 4–6h.' },
  { max: 6,  risk: 'modere',   label: 'Modéré', desc: 'Score 5–6. Surveillance continue, appeler le médecin.' },
  { max: 20, risk: 'critique', label: 'Élevé',  desc: 'Score ≥ 7. Urgence — activer l\'équipe médicale de réanimation.' },
];

const RASS_OPTIONS = [
  { value: +4, risk: 'critique', label: 'Combatif',           desc: 'Violent, danger pour le personnel' },
  { value: +3, risk: 'eleve',    label: 'Très agité',         desc: 'Arrache les dispositifs médicaux, agressif' },
  { value: +2, risk: 'eleve',    label: 'Agité',              desc: 'Mouvements fréquents non intentionnels' },
  { value: +1, risk: 'modere',   label: 'Agitation légère',   desc: 'Anxieux, mouvements peu fréquents' },
  { value:  0, risk: 'normal',   label: 'Éveillé et calme',   desc: 'État cible — alerte et calme' },
  { value: -1, risk: 'faible',   label: 'Somnolent',          desc: 'Pas tout à fait éveillé, réveillé > 10 sec sur stimulation verbale' },
  { value: -2, risk: 'faible',   label: 'Sédation légère',    desc: 'Brièvement éveillé (< 10 sec) sur stimulation verbale' },
  { value: -3, risk: 'modere',   label: 'Sédation modérée',   desc: 'Mouvement ou ouverture des yeux sans contact visuel' },
  { value: -4, risk: 'eleve',    label: 'Sédation profonde',  desc: 'Pas de réponse à la voix, mouvement à la stimulation douloureuse' },
  { value: -5, risk: 'critique', label: 'Non réveillable',    desc: 'Aucune réponse à la voix ou à la douleur' },
];

const ALGOPLUS_ITEMS = [
  { id: 'visage',        label: 'Visage',                        desc: 'Front plissé, grimaces, crispation, mâchoires serrées',                points: 1 },
  { id: 'regard',        label: 'Regard',                        desc: 'Regard tendu, fixe, lointain ou présence de larmes',                   points: 1 },
  { id: 'plaintes',      label: 'Plaintes orales',               desc: 'Plaintes, gémissements, cris, appels répétés',                         points: 1 },
  { id: 'attitudes',     label: 'Attitudes corporelles',         desc: 'Agitation, repli sur soi, position antalgique évidente',               points: 1 },
  { id: 'comportements', label: 'Comportements inhabituels',     desc: 'Résistance aux soins, agitation lors des mobilisations, opposition',   points: 1 },
];
const ALGOPLUS_LEVELS = [
  { max: 1, risk: 'normal', label: 'Absence de douleur', desc: 'Score 0–1 : douleur absente ou minime. Réévaluer régulièrement.' },
  { max: 5, risk: 'eleve',  label: 'Douleur probable',   desc: 'Score ≥ 2 : douleur probable. Traitement antalgique et réévaluation.' },
];

const BRADEN_DATA = {
  groups: [
    { id: 'perception', label: 'Perception sensorielle', options: [
      { value: 1, label: 'Complètement limitée', desc: 'Aucune réponse aux stimuli douloureux' },
      { value: 2, label: 'Très limitée',          desc: 'Réponse seulement aux stimuli douloureux' },
      { value: 3, label: 'Légèrement limitée',    desc: 'Répond aux ordres verbaux, déficit sensoriel partiel' },
      { value: 4, label: 'Intacte',               desc: 'Répond normalement, pas de déficit sensoriel' },
    ]},
    { id: 'humidite', label: 'Humidité cutanée', options: [
      { value: 1, label: 'Constamment humide', desc: 'Peau presque toujours humide (transpiration, urine)' },
      { value: 2, label: 'Très humide',        desc: 'Peau souvent humide, draps à changer fréquemment' },
      { value: 3, label: 'Parfois humide',     desc: 'Peau parfois humide, change environ 1×/jour' },
      { value: 4, label: 'Rarement humide',    desc: 'Peau généralement sèche, changes de routine' },
    ]},
    { id: 'activite', label: 'Activité physique', options: [
      { value: 1, label: 'Alité',              desc: 'Confiné au lit en permanence' },
      { value: 2, label: 'En chaise',          desc: 'Marche très limitée, passe surtout au fauteuil' },
      { value: 3, label: 'Marche parfois',     desc: 'Marche au moins 2 fois/jour, courtes distances' },
      { value: 4, label: 'Marche régulièrement', desc: 'Marche fréquemment hors de la chambre' },
    ]},
    { id: 'mobilite', label: 'Mobilité', options: [
      { value: 1, label: 'Complètement immobile', desc: 'Aucun changement de position sans aide' },
      { value: 2, label: 'Très limitée',           desc: 'Changements de position très limités' },
      { value: 3, label: 'Légèrement limitée',     desc: 'Changements fréquents mais limités' },
      { value: 4, label: 'Sans limitation',        desc: 'Changements de position majeurs seul' },
    ]},
    { id: 'nutrition', label: 'Nutrition', options: [
      { value: 1, label: 'Très insuffisante', desc: 'Mange < 1/3 des repas, peu de protéines' },
      { value: 2, label: 'Insuffisante',      desc: 'Mange rarement le repas complet, peu de protéines' },
      { value: 3, label: 'Adéquate',          desc: 'Mange > 1/2 des repas, protéines suffisantes' },
      { value: 4, label: 'Excellente',        desc: 'Mange les repas complets, protéines en quantité' },
    ]},
    { id: 'friction', label: 'Friction et cisaillement', options: [
      { value: 1, label: 'Problème',                 desc: 'Nécessite une aide totale pour se déplacer' },
      { value: 2, label: 'Problème potentiel',        desc: 'Se déplace avec difficulté, glissements possibles' },
      { value: 3, label: 'Pas de problème apparent', desc: 'Se déplace seul avec suffisamment de force' },
    ]},
  ],
  min: 6, max: 23,
  levels: [
    { max: 9,  risk: 'critique', label: 'Risque très élevé', desc: 'Protocole de prévention intensif : matelas spécialisé, retournements toutes les 2h, soins cutanés.' },
    { max: 12, risk: 'eleve',    label: 'Risque élevé',      desc: 'Matelas anti-escarre, retournements réguliers, protection des zones à risque.' },
    { max: 14, risk: 'modere',   label: 'Risque modéré',     desc: 'Prévention renforcée, soins cutanés, surveillance rapprochée.' },
    { max: 18, risk: 'faible',   label: 'Risque faible',     desc: 'Surveillance standard, prévention de base.' },
    { max: 23, risk: 'normal',   label: 'Pas de risque',     desc: 'Pas de risque significatif d\'escarre détecté.' },
  ],
};

const MORSE_DATA = {
  groups: [
    { id: 'atcd', label: 'Antécédents de chute (3 mois)', options: [
      { value: 0,  label: 'Non', desc: 'Aucune chute récente' },
      { value: 25, label: 'Oui', desc: 'Au moins une chute dans les 3 derniers mois' },
    ]},
    { id: 'diag', label: 'Diagnostic secondaire', options: [
      { value: 0,  label: 'Non', desc: 'Un seul diagnostic médical' },
      { value: 15, label: 'Oui', desc: 'Plus d\'un diagnostic médical actif' },
    ]},
    { id: 'aide', label: 'Aide à la marche', options: [
      { value: 0,  label: 'Aucune / Alité',            desc: 'Sans aide technique ou alité' },
      { value: 15, label: 'Béquilles / Canne',         desc: 'Aide technique à la marche' },
      { value: 30, label: 'S\'appuie sur les meubles', desc: 'Marche en se cramponant aux meubles' },
    ]},
    { id: 'perf', label: 'Perfusion IV / Héparine lock', options: [
      { value: 0,  label: 'Non', desc: 'Pas de dispositif veineux' },
      { value: 20, label: 'Oui', desc: 'Perfusion ou voie veineuse périphérique en place' },
    ]},
    { id: 'demarche', label: 'Démarche / Transfert', options: [
      { value: 0,  label: 'Normale / FR / Alité', desc: 'Démarche normale ou se déplace en fauteuil roulant' },
      { value: 10, label: 'Faible',               desc: 'Démarche lente, peut se redresser seul' },
      { value: 20, label: 'Altérée',              desc: 'Difficultés à se redresser, cramponné aux meubles' },
    ]},
    { id: 'mental', label: 'État mental', options: [
      { value: 0,  label: 'Orienté',                  desc: 'Conscient de ses capacités physiques' },
      { value: 15, label: 'Surévalue ses capacités', desc: 'Oublie ses limitations, surestime ses capacités' },
    ]},
  ],
  min: 0, max: 125,
  levels: [
    { max: 24,  risk: 'normal', label: 'Risque faible',  desc: 'Soins de base. Réévaluer à chaque changement d\'état clinique.' },
    { max: 50,  risk: 'modere', label: 'Risque modéré',  desc: 'Mesures préventives : lit en position basse, sonnette à portée, chaussures antidérapantes.' },
    { max: 125, risk: 'eleve',  label: 'Risque élevé',   desc: 'Protocole chute complet. Signaler au dossier, bracelet de signalisation, surveillance renforcée.' },
  ],
};

const NRS_ITEMS = [
  { id: 'imc',      label: 'IMC < 20,5 kg/m²',                              desc: 'Indice de masse corporelle bas',               points: 1 },
  { id: 'poids',    label: 'Perte de poids dans les 3 derniers mois',        desc: 'Amaigrissement récent involontaire',           points: 1 },
  { id: 'apport',   label: 'Diminution des apports alimentaires (1 semaine)', desc: 'Réduction de la prise alimentaire récente',  points: 1 },
  { id: 'severite', label: 'Patient sévèrement atteint',                     desc: 'Réanimation, cancer, chirurgie lourde, BPCO sévère…', points: 1 },
  { id: 'age',      label: 'Âge ≥ 70 ans',                                   desc: 'Facteur de majoration automatique (+1 point)', points: 1 },
];
const NRS_LEVELS = [
  { max: 2, risk: 'normal', label: 'Pas de risque',   desc: 'Score < 3 : pas de risque nutritionnel immédiat. Réévaluer chaque semaine.' },
  { max: 7, risk: 'eleve',  label: 'À risque',        desc: 'Score ≥ 3 : risque de dénutrition. Prise en charge nutritionnelle et avis diététique à initier.' },
];

const BARTHEL_DATA = {
  groups: [
    { id: 'alimentation', label: 'Alimentation', options: [
      { value: 0,  label: 'Dépendant',       desc: 'Aide totale pour manger' },
      { value: 5,  label: 'Aide partielle',  desc: 'Aide pour couper, beurrer, installer' },
      { value: 10, label: 'Indépendant',     desc: 'Mange seul sur plateau ou table' },
    ]},
    { id: 'bain', label: 'Bain / Douche', options: [
      { value: 0, label: 'Dépendant',    desc: 'Besoin d\'aide' },
      { value: 5, label: 'Indépendant',  desc: 'Sans surveillance ni aide' },
    ]},
    { id: 'toilette', label: 'Toilette / Hygiène', options: [
      { value: 0, label: 'Dépendant',    desc: 'Besoin d\'aide' },
      { value: 5, label: 'Indépendant',  desc: 'Se lave le visage, les dents, se coiffe seul' },
    ]},
    { id: 'habillage', label: 'Habillage', options: [
      { value: 0,  label: 'Dépendant',       desc: 'Aide totale nécessaire' },
      { value: 5,  label: 'Aide partielle',  desc: 'Réalise environ la moitié des tâches' },
      { value: 10, label: 'Indépendant',     desc: 'S\'habille seul y compris les lacets' },
    ]},
    { id: 'intestin', label: 'Continence intestinale', options: [
      { value: 0,  label: 'Incontinent',            desc: 'Incontinence fécale' },
      { value: 5,  label: 'Accidents occasionnels', desc: 'Moins d\'une fois par semaine' },
      { value: 10, label: 'Continent',              desc: 'Continence totale' },
    ]},
    { id: 'vesicale', label: 'Continence vésicale', options: [
      { value: 0,  label: 'Incontinent',            desc: 'Incontinence urinaire' },
      { value: 5,  label: 'Accidents occasionnels', desc: 'Maximum 1 fois par 24h' },
      { value: 10, label: 'Continent',              desc: 'Continence totale' },
    ]},
    { id: 'toilettes', label: 'Utilisation des toilettes', options: [
      { value: 0,  label: 'Dépendant',       desc: 'Aide totale nécessaire' },
      { value: 5,  label: 'Aide partielle',  desc: 'Besoin d\'aide pour l\'équilibre ou l\'habillage' },
      { value: 10, label: 'Indépendant',     desc: 'S\'installe, s\'habille, se nettoie seul' },
    ]},
    { id: 'transfert', label: 'Transferts (lit ↔ fauteuil)', options: [
      { value: 0,  label: 'Dépendant',      desc: 'Aide totale — ne peut pas s\'asseoir' },
      { value: 5,  label: 'Aide majeure',   desc: 'Une ou deux personnes requises' },
      { value: 10, label: 'Aide mineure',   desc: 'Surveillance ou aide verbale' },
      { value: 15, label: 'Indépendant',    desc: 'Sans aide ni surveillance' },
    ]},
    { id: 'mobilite', label: 'Mobilité (≥ 50 m)', options: [
      { value: 0,  label: 'Immobile',       desc: 'Ne marche pas' },
      { value: 5,  label: 'Fauteuil roulant', desc: 'Se déplace seul en FR sur 50 m' },
      { value: 10, label: 'Aide requise',   desc: 'Marche avec aide d\'une personne' },
      { value: 15, label: 'Indépendant',    desc: 'Marche seul sur 50 m (canne acceptée)' },
    ]},
    { id: 'escaliers', label: 'Escaliers', options: [
      { value: 0,  label: 'Dépendant',      desc: 'Ne monte pas les escaliers' },
      { value: 5,  label: 'Aide requise',   desc: 'Besoin d\'aide ou surveillance' },
      { value: 10, label: 'Indépendant',    desc: 'Monte et descend seul' },
    ]},
  ],
  min: 0, max: 100,
  levels: [
    { max: 20,  risk: 'critique', label: 'Dépendance totale',   desc: 'Dépendance totale. Soins complets, aide à tous les AVQ.' },
    { max: 60,  risk: 'eleve',    label: 'Dépendance sévère',   desc: 'Aide importante dans la majorité des activités.' },
    { max: 90,  risk: 'modere',   label: 'Dépendance modérée',  desc: 'Aide partielle. Rééducation fonctionnelle à prévoir.' },
    { max: 99,  risk: 'faible',   label: 'Dépendance légère',   desc: 'Quasi-indépendant, peu d\'aide nécessaire.' },
    { max: 100, risk: 'normal',   label: 'Indépendant',         desc: 'Indépendance complète dans les activités de la vie quotidienne.' },
  ],
};

// ─── Helper components ────────────────────────────────

function ResultPanel({ score, level, min, max, complete = true }) {
  if (score === null || level === null) {
    return (
      <div className="flex flex-col items-center justify-center h-28 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700">
        <p className="text-sm text-slate-400 dark:text-slate-500">Complétez le score pour voir le résultat</p>
      </div>
    );
  }
  const style = RISK_STYLE[level.risk];
  const pct = Math.max(0, Math.min(100, Math.round(((score - min) / (max - min)) * 100)));

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 space-y-4">
      <div className="flex items-end gap-3">
        <span className="text-5xl font-black text-slate-800 dark:text-white tabular-nums leading-none">{score}</span>
        <div className="mb-1 flex flex-col gap-0.5">
          <span className="text-xs text-slate-400">/ {max}</span>
          {!complete && <span className="text-[10px] text-amber-500 font-semibold uppercase tracking-wide">Incomplet</span>}
        </div>
        <div className={`ml-auto px-3 py-1.5 rounded-full text-xs font-bold tracking-wide uppercase ${style.bg} ${style.text}`}>
          {level.label}
        </div>
      </div>

      <div className="h-3 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${style.bar} transition-all duration-700 ease-out`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{level.desc}</p>
    </div>
  );
}

function OptionGroup({ group, value, onChange }) {
  const cols = group.options.length <= 2 ? 'grid-cols-2' : group.options.length === 3 ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4';
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{group.label}</p>
        {value !== undefined && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 font-bold">
            {value} pt{Math.abs(value) > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className={`grid ${cols} gap-2`}>
        {group.options.map(opt => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`text-left p-3 rounded-xl border-2 transition-all ${
              value === opt.value
                ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 shadow-sm'
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-teal-300 dark:hover:border-teal-700'
            }`}
          >
            <span className={`font-bold text-sm block ${value === opt.value ? 'text-teal-700 dark:text-teal-300' : 'text-slate-700 dark:text-slate-300'}`}>
              {opt.label}
            </span>
            {opt.desc && (
              <span className="text-[11px] text-slate-400 dark:text-slate-500 leading-tight block mt-0.5">
                {opt.desc}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function CheckRow({ id, label, desc, points, checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`w-full flex items-start gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-all ${
        checked
          ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-teal-300 dark:hover:border-teal-700'
      }`}
    >
      <div className={`shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all mt-0.5 ${
        checked ? 'border-teal-500 bg-teal-500' : 'border-slate-300 dark:border-slate-600'
      }`}>
        {checked && <Check size={11} className="text-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium leading-tight ${checked ? 'text-teal-700 dark:text-teal-300' : 'text-slate-700 dark:text-slate-300'}`}>
          {label}
        </p>
        {desc && <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{desc}</p>}
      </div>
      <span className={`shrink-0 text-sm font-bold px-2.5 py-1 rounded-lg transition-all ${
        checked ? 'bg-teal-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-400'
      }`}>
        +{points}
      </span>
    </button>
  );
}

// ─── Calculateurs ─────────────────────────────────────

function OptionGroupCalc({ data }) {
  const [values, setValues] = useState({});
  const total = Object.values(values).reduce((a, b) => a + b, 0);
  const completed = Object.keys(values).length === data.groups.length;
  const score = completed || Object.keys(values).length > 0 ? total : null;
  const level = score !== null ? (data.levels.find(l => score <= l.max) || data.levels[data.levels.length - 1]) : null;

  return (
    <div className="space-y-5">
      {data.groups.map(group => (
        <OptionGroup
          key={group.id}
          group={group}
          value={values[group.id]}
          onChange={v => setValues(prev => ({ ...prev, [group.id]: v }))}
        />
      ))}
      <ResultPanel score={score} level={level} min={data.min} max={data.max} complete={completed} />
    </div>
  );
}

function ChecklistCalc({ items, levels }) {
  const [checked, setChecked] = useState({});
  const score = items.reduce((acc, item) => acc + (checked[item.id] ? item.points : 0), 0);
  const level = levels.find(l => score <= l.max) || levels[levels.length - 1];
  const max = items.reduce((a, i) => a + i.points, 0);

  return (
    <div className="space-y-3">
      {items.map(item => (
        <CheckRow
          key={item.id}
          {...item}
          checked={!!checked[item.id]}
          onChange={v => setChecked(prev => ({ ...prev, [item.id]: v }))}
        />
      ))}
      <ResultPanel score={score} level={level} min={0} max={max} complete={true} />
    </div>
  );
}

function GCSCalc() {
  return <OptionGroupCalc data={GCS_DATA} />;
}

function qSOFACalc() {
  return <ChecklistCalc items={QSOFA_ITEMS} levels={QSOFA_LEVELS} />;
}

function NEWS2Calc() {
  const [values, setValues] = useState({});

  const scores = NEWS2_PARAMS.map(p => {
    const v = values[p.id];
    if (v === undefined || v === '') return null;
    const num = p.type === 'number' ? parseFloat(v) : v;
    if (p.type === 'number' && isNaN(num)) return null;
    return p.score(num);
  });

  const allFilled = scores.every(s => s !== null);
  const total = allFilled ? scores.reduce((a, b) => a + b, 0) : null;
  const level = total !== null ? (NEWS2_LEVELS.find(l => total <= l.max) || NEWS2_LEVELS[NEWS2_LEVELS.length - 1]) : null;

  const getScoreColor = s => {
    if (s === null) return '';
    if (s === 0) return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300';
    if (s === 1) return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
    return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {NEWS2_PARAMS.map((param, i) => {
          const s = scores[i];
          return (
            <div key={param.id} className={`p-3.5 rounded-xl border-2 transition-all ${
              s !== null ? 'border-teal-300 dark:border-teal-700 bg-teal-50/30 dark:bg-teal-900/10' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
            }`}>
              <div className="flex items-center justify-between mb-2.5">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">{param.label}</label>
                {s !== null && (
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${getScoreColor(s)}`}>
                    {s === 0 ? '0' : `+${s}`} pt{s > 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {param.type === 'number' && (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step={param.step || 1}
                    value={values[param.id] || ''}
                    onChange={e => setValues(prev => ({ ...prev, [param.id]: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                    placeholder="—"
                  />
                  <span className="text-xs text-slate-400 whitespace-nowrap">{param.unit}</span>
                </div>
              )}

              {param.type === 'toggle' && (
                <div className="flex gap-2">
                  {param.options.map(opt => (
                    <button
                      key={opt}
                      onClick={() => setValues(prev => ({ ...prev, [param.id]: opt }))}
                      className={`flex-1 py-1.5 rounded-lg text-sm font-medium transition ${
                        values[param.id] === opt
                          ? 'bg-teal-500 text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}

              {param.type === 'select' && (
                <select
                  value={values[param.id] || ''}
                  onChange={e => setValues(prev => ({ ...prev, [param.id]: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                >
                  <option value="">Sélectionner…</option>
                  {param.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              )}
            </div>
          );
        })}
      </div>

      <ResultPanel score={total} level={level} min={0} max={20} complete={allFilled} />
    </div>
  );
}

function RASSCalc() {
  const [selected, setSelected] = useState(null);
  const opt = selected !== null ? RASS_OPTIONS.find(o => o.value === selected) : null;

  return (
    <div className="space-y-2">
      {RASS_OPTIONS.map(o => {
        const style = RISK_STYLE[o.risk];
        const isSelected = selected === o.value;
        return (
          <button
            key={o.value}
            onClick={() => setSelected(o.value)}
            className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl border-2 text-left transition-all ${
              isSelected
                ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 shadow-sm'
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-teal-300 dark:hover:border-teal-700'
            }`}
          >
            <span className={`font-mono text-lg font-black w-9 text-center shrink-0 ${isSelected ? 'text-teal-600 dark:text-teal-400' : 'text-slate-300 dark:text-slate-600'}`}>
              {o.value >= 0 ? `+${o.value}` : o.value}
            </span>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${isSelected ? 'text-teal-700 dark:text-teal-300' : 'text-slate-700 dark:text-slate-300'}`}>
                {o.label}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 leading-tight mt-0.5">{o.desc}</p>
            </div>
            <span className={`shrink-0 text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide ${style.bg} ${style.text}`}>
              {o.risk}
            </span>
          </button>
        );
      })}

      {opt && (
        <div className={`mt-2 p-4 rounded-xl ${RISK_STYLE[opt.risk].bg} flex items-center gap-4`}>
          <span className={`text-4xl font-black tabular-nums ${RISK_STYLE[opt.risk].text}`}>
            {selected >= 0 ? `+${selected}` : selected}
          </span>
          <div>
            <p className={`font-bold ${RISK_STYLE[opt.risk].text}`}>{opt.label}</p>
            <p className={`text-sm ${RISK_STYLE[opt.risk].text} opacity-80 mt-0.5`}>{opt.desc}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function DouleurCalc() {
  const [score, setScore] = useState(null);

  const getLevel = s => {
    if (s === 0) return { risk: 'normal',   label: 'Absente',  desc: 'Aucune douleur.' };
    if (s <= 3)  return { risk: 'faible',   label: 'Légère',   desc: 'Douleur légère. Traitement non médicamenteux à privilégier.' };
    if (s <= 6)  return { risk: 'modere',   label: 'Modérée',  desc: 'Douleur modérée. Antalgiques palier 1 ou 2 selon prescription.' };
    return           { risk: 'critique', label: 'Sévère',   desc: 'Douleur sévère (≥ 7). Antalgiques palier 2–3, alerter le médecin.' };
  };

  const btnColor = n => {
    if (n === 0)     return score === n ? 'bg-emerald-500 text-white shadow-lg scale-110' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:scale-105';
    if (n <= 3)      return score === n ? 'bg-teal-500 text-white shadow-lg scale-110'    : 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 hover:scale-105';
    if (n <= 6)      return score === n ? 'bg-amber-500 text-white shadow-lg scale-110'   : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:scale-105';
    return                  score === n ? 'bg-red-500 text-white shadow-lg scale-110'     : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:scale-105';
  };

  const level = score !== null ? getLevel(score) : null;
  const style = level ? RISK_STYLE[level.risk] : null;

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-slate-200 dark:border-slate-700 p-6">
        <p className="text-sm text-slate-500 dark:text-slate-400 italic mb-6">
          "De 0 à 10, quelle est votre douleur en ce moment ?"
        </p>
        <div className="flex gap-1.5 justify-between mb-3">
          {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
            <button
              key={n}
              onClick={() => setScore(n)}
              className={`flex-1 aspect-square rounded-xl text-sm font-bold transition-all ${btnColor(n)}`}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="flex justify-between text-xs text-slate-400">
          <span>Aucune douleur</span>
          <span>Douleur maximale</span>
        </div>
      </div>

      {score !== null && level && style && (
        <div className={`p-5 rounded-2xl border ${style.bg} flex items-center gap-5`}>
          <div className={`text-6xl font-black tabular-nums leading-none ${style.text}`}>{score}</div>
          <div>
            <p className={`font-bold text-lg ${style.text}`}>{level.label}</p>
            <p className={`text-sm mt-1 ${style.text} opacity-80`}>{level.desc}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function AlgoplusCalc() {
  return <ChecklistCalc items={ALGOPLUS_ITEMS} levels={ALGOPLUS_LEVELS} />;
}

function BradenCalc() {
  return <OptionGroupCalc data={BRADEN_DATA} />;
}

function MorseCalc() {
  return <OptionGroupCalc data={MORSE_DATA} />;
}

function NRSCalc() {
  return <ChecklistCalc items={NRS_ITEMS} levels={NRS_LEVELS} />;
}

function BarthelCalc() {
  return <OptionGroupCalc data={BARTHEL_DATA} />;
}

const CALC_MAP = {
  gcs:      GCSCalc,
  qsofa:    qSOFACalc,
  news2:    NEWS2Calc,
  rass:     RASSCalc,
  douleur:  DouleurCalc,
  algoplus: AlgoplusCalc,
  braden:   BradenCalc,
  morse:    MorseCalc,
  nrs:      NRSCalc,
  barthel:  BarthelCalc,
};

// ─── Page principale ──────────────────────────────────
export default function ScoresCliniquesPage() {
  const { theme } = useTheme();
  const navigate  = useNavigate();
  const isDark    = theme === 'dark';

  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedScore,  setSelectedScore]  = useState('gcs');
  const [search, setSearch] = useState('');

  const filtered = SCORES_META.filter(s => {
    const matchCat    = activeCategory === 'all' || s.category === activeCategory;
    const matchSearch = search === '' ||
      s.title.toLowerCase().includes(search.toLowerCase()) ||
      s.abbrev.toLowerCase().includes(search.toLowerCase()) ||
      s.desc.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const selected = SCORES_META.find(s => s.id === selectedScore);
  const CalcComponent = selectedScore ? CALC_MAP[selectedScore] : null;

  return (
    <div className={`${isDark ? 'dark' : ''}`}>
      <div className="flex flex-col h-screen bg-slate-50 dark:bg-slate-950 transition-colors">

        {/* ── Header ── */}
        <header className="shrink-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm z-10">
          <div className="px-4 py-3 flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 transition text-sm shrink-0"
            >
              <ArrowLeft size={18} />
              <span className="hidden sm:inline">Retour</span>
            </button>

            <div className="h-5 w-px bg-slate-200 dark:bg-slate-700 shrink-0" />

            <div className="flex-1 min-w-0">
              <h1 className="font-bold text-slate-800 dark:text-slate-100 text-base leading-tight">
                Scores Cliniques
              </h1>
              <p className="text-xs text-slate-400 dark:text-slate-500 hidden sm:block">
                {SCORES_META.length} scores disponibles — Urgence · Douleur · Risques · Autonomie · Nutrition · Sédation
              </p>
            </div>

            {/* Barre de recherche */}
            <div className="relative w-full max-w-xs shrink-0 hidden sm:block">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher un score…"
                className="w-full pl-8 pr-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-300 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
              />
            </div>
          </div>

          {/* Filtres catégories */}
          <div className="px-4 pb-3 flex gap-2 overflow-x-auto scrollbar-hide">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    isActive
                      ? 'bg-teal-500 text-white shadow-sm'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {Icon && <Icon size={12} />}
                  {cat.label}
                </button>
              );
            })}
          </div>
        </header>

        {/* ── Corps ── */}
        <div className="flex flex-1 overflow-hidden">

          {/* ── Sidebar ── */}
          <aside className="w-72 shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-y-auto hidden md:flex flex-col">
            {/* Recherche mobile/sidebar */}
            <div className="p-3 border-b border-slate-100 dark:border-slate-800 sm:hidden">
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher…"
                  className="w-full pl-8 pr-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                />
              </div>
            </div>

            {/* Liste des scores */}
            <div className="flex-1 p-3 space-y-3 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-10">Aucun score trouvé</p>
              ) : (
                CATEGORIES.filter(c => c.id !== 'all').map(cat => {
                  const catScores = filtered.filter(s => s.category === cat.id);
                  if (catScores.length === 0) return null;
                  const cs = CAT_STYLE[cat.id];
                  const CatIcon = cat.icon;
                  return (
                    <div key={cat.id} className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
                      {/* En-tête catégorie */}
                      <div className={`flex items-center gap-2 px-3 py-2 ${cs.bg}`}>
                        {CatIcon && <CatIcon size={12} className={cs.text} />}
                        <span className={`text-[10px] font-bold uppercase tracking-widest flex-1 ${cs.text}`}>
                          {cat.label}
                        </span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-white/60 dark:bg-slate-900/40 ${cs.text}`}>
                          {catScores.length}
                        </span>
                      </div>
                      {/* Scores de la catégorie */}
                      <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {catScores.map(score => {
                          const isActive = selectedScore === score.id;
                          return (
                            <button
                              key={score.id}
                              onClick={() => setSelectedScore(score.id)}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all ${
                                isActive
                                  ? `${cs.bg} ${cs.accent}`
                                  : 'bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800'
                              }`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-semibold truncate ${isActive ? cs.text : 'text-slate-700 dark:text-slate-300'}`}>
                                  {score.abbrev}
                                  <span className={`ml-1.5 font-normal text-xs ${isActive ? cs.text : 'text-slate-400'}`}>
                                    — {score.desc}
                                  </span>
                                </p>
                                <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 font-mono">{score.range}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="p-3 border-t border-slate-100 dark:border-slate-800">
              <p className="text-[10px] text-slate-400 dark:text-slate-600 text-center leading-relaxed">
                {filtered.length} score{filtered.length > 1 ? 's' : ''} · Usage professionnel uniquement
              </p>
            </div>
          </aside>

          {/* ── Zone calculateur ── */}
          <main className="flex-1 overflow-y-auto">
            {selected && CalcComponent ? (
              <div className="max-w-3xl mx-auto px-4 py-6">

                {/* Bandeau score */}
                <div className={`rounded-2xl border p-5 mb-6 ${CAT_STYLE[selected.category].bg} ${CAT_STYLE[selected.category].border || 'border-transparent'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold uppercase tracking-wider ${CAT_STYLE[selected.category].text}`}>
                          {CATEGORIES.find(c => c.id === selected.category)?.label}
                        </span>
                        <span className="text-slate-300 dark:text-slate-600">·</span>
                        <span className={`text-xs font-mono ${CAT_STYLE[selected.category].text}`}>{selected.range}</span>
                      </div>
                      <h2 className="text-xl font-black text-slate-800 dark:text-white leading-tight">{selected.title}</h2>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{selected.desc}</p>
                    </div>
                    <div className={`shrink-0 text-2xl font-black px-4 py-2 rounded-xl bg-white/60 dark:bg-slate-900/40 ${CAT_STYLE[selected.category].text}`}>
                      {selected.abbrev}
                    </div>
                  </div>
                </div>

                {/* Mobile: sélecteur de score groupé par catégorie */}
                <div className="md:hidden mb-4">
                  <select
                    value={selectedScore}
                    onChange={e => setSelectedScore(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                  >
                    {CATEGORIES.filter(c => c.id !== 'all').map(cat => {
                      const catScores = filtered.filter(s => s.category === cat.id);
                      if (catScores.length === 0) return null;
                      return (
                        <optgroup key={cat.id} label={cat.label}>
                          {catScores.map(s => (
                            <option key={s.id} value={s.id}>{s.abbrev} — {s.desc}</option>
                          ))}
                        </optgroup>
                      );
                    })}
                  </select>
                </div>

                {/* Calculateur */}
                <div key={selectedScore}>
                  <CalcComponent />
                </div>

                {/* Disclaimer */}
                <div className="mt-8 flex items-start gap-2 text-xs text-slate-400 dark:text-slate-500 border-t border-slate-200 dark:border-slate-800 pt-4">
                  <AlertTriangle size={13} className="shrink-0 mt-0.5 text-amber-400" />
                  <span>
                    Ces scores sont des outils d'aide à la décision. Ils ne remplacent pas le jugement clinique.
                    Vérifiez systématiquement avec les protocoles de votre établissement et l'équipe médicale.
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                <BarChart2 size={48} className="text-slate-300 dark:text-slate-700" />
                <p className="text-slate-400 dark:text-slate-500 text-sm">Sélectionnez un score dans la liste</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
